import React, { useState, useEffect } from "react";
import "../styles/intake.css";
import ServiceSelector from "../components/forms/ServiceSelector";
import MachineSelector from "../components/forms/MachineSelector";
import ProductSelector from "../components/forms/ProductSelector";

export default function ServicesForm({ formData, setFormData }) {
  const [servicesList, setServicesList] = useState([]);
  const [isEditing, setIsEditing] = useState(true);

  useEffect(() => {
    if (!formData.clinic_id) return;

    const fetchServices = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/services/get-services?clinic_id=${formData.clinic_id}`);
        const result = await res.json();

        console.log("Returned data:", result);

        if (!Array.isArray(result)) {
          throw new Error("Invalid response");
        }

        const transformed = result.map(s => ({
          ...s,
          images: s.images || [null, null, null],
          machinesUsed: s.machines_used || [],
          productsUsed: s.products_used || [],
        }));

        setServicesList(transformed);
      } catch (err) {
        console.error("Error loading services:", err);
      }
    };

    fetchServices();
  }, [formData.clinic_id]);

  const addService = () => {
    setServicesList([
      ...servicesList,
      {
        name: "",
        duration: "",
        price: "",
        machinesUsed: [],
        productsUsed: [],
        images: [null, null, null],
        id: null,
      },
    ]);
  };

  const updateService = (index, field, value) => {
    const updated = [...servicesList];
    updated[index][field] = value;
    setServicesList(updated);
  };

  const removeService = async (index) => {
    const service = servicesList[index];
  
    // If the service has an ID, delete from backend
    if (service.id) {
      const confirmed = window.confirm("Are you sure you want to delete this service?");
      if (!confirmed) return;
  
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/services/delete-service/${service.id}`,
          {
            method: "DELETE",
          }
        );
  
        const result = await res.json();
  
        if (!res.ok) {
          throw new Error(result.message || "Failed to delete service");
        }
  
        alert("✅ Service deleted successfully!");
      } catch (err) {
        alert("❌ Failed to delete from backend: " + err.message);
        return;
      }
    }
  
    // Always remove from frontend
    const updated = [...servicesList];
    updated.splice(index, 1);
    setServicesList(updated);
  };


  const handleDeleteImage = async (serviceId, imageObj, serviceIndex, imageIndex) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/services/delete-service-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_id: serviceId,
          file_url: imageObj.file_url,
        }),
      });
  
      if (!res.ok) {
        const err = await res.json();
        console.error("Delete failed:", err.detail || "Unknown error");
        alert("Failed to delete image.");
        return;
      }
  
      // Remove image from local state
      const updated = [...servicesList];
      updated[serviceIndex].images[imageIndex] = null;
      setServicesList(updated);
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Something went wrong while deleting.");
    }
  };
  

  const handleSave = async () => {
    for (const [index, service] of servicesList.entries()) {
      if (!formData.clinic_id || !service.name || !service.duration || !service.price) {
        alert(`❗ Service #${index + 1} is missing required fields (Name, Duration, Price).`);
        return;
      }
  
      const payload = {
        clinic_id: formData.clinic_id,
        name: service.name,
        duration: parseInt(service.duration),
        price: parseFloat(service.price),
        machines_used: service.machinesUsed,
        products_used: service.productsUsed,
        images: service.images.filter((img) => img?.file_url),
      };

      if (service.id) {
        payload.id = service.id; // ✅ Include ID to avoid duplicate inserts
      }
      
  
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/services/save-service`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
  
        const result = await res.json();
  
        if (!res.ok) {
          const errorMsg = result?.detail || result?.message || "Unknown error from server";
          throw new Error(errorMsg);
        }
  
        const updated = [...servicesList];
        updated[index].id = result.service_id;
        setServicesList(updated);
      } catch (err) {
        alert(`❌ Failed to save service #${index + 1}: ${err.message}`);
        return;
      }
    }
  
    alert("✅ All services saved successfully!");
    setIsEditing(false);
  };
  


  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleFileUpload = async (file, clinicId, serviceId, pairIndex, serviceIndex) => {
    // Step 1: Save the service if it doesn't have an ID
    if (!serviceId) {
      const service = servicesList[serviceIndex];
      if (!clinicId || !service.name || !service.duration || !service.price) {
        alert("❗ Please fill in required fields (Name, Duration, Price) before uploading images.");
        return null;
      }
  
      const payload = {
        clinic_id: clinicId,
        name: service.name,
        duration: parseInt(service.duration),
        price: parseFloat(service.price),
        machines_used: service.machinesUsed,
        products_used: service.productsUsed,
        images: service.images.filter((img) => img?.file_url),
      };
  
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/services/save-service`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
  
        const resultText = await res.text();
        let result;
  
        try {
          result = JSON.parse(resultText);
        } catch {
          result = { raw: resultText };
        }
  
        console.log("🔥 Save-service response:", result);
  
        if (!res.ok) {
          const errorMsg = result?.detail || result?.message || result?.raw || `Unknown error (status ${res.status})`;
          throw new Error(errorMsg);
        }
  
        const updated = [...servicesList];
        updated[serviceIndex].id = result.service_id;
        setServicesList(updated);
        serviceId = result.service_id;
      } catch (err) {
        alert("❌ Failed to save service before upload: " + err.message);
        console.error("❌ Save service error:", err);
        return null;
      }
    }
  
    // Step 2: Upload image
    const formData = new FormData();
    formData.append("file", file);
    formData.append("clinic_id", clinicId);
    formData.append("service_id", serviceId);
  
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/services/upload-service-image`, {
        method: "POST",
        body: formData,
      });
  
      const result = await response.json();
  
      if (response.ok) {
        alert("✅ Image uploaded successfully!");
        return result.url ? { file_name: result.file_name, file_url: result.url } : null;
      } else {
        throw new Error(result.error || result.message || "Upload failed");
      }
    } catch (err) {
      alert("❌ Upload error: " + err.message);
      console.error("🚨 Upload failed:", err);
      return null;
    }
  };
  
  

  return (
    <div className="form-wrapper">
      <div className="form-section-header">
        <h2>3. <span className="section-title">Services</span></h2>
        <div className="form-buttons">
          {isEditing ? (
            <button className="blue-button" onClick={handleSave}>Save</button>
          ) : (
            <button className="blue-button" onClick={handleEdit}>Edit</button>
          )}
        </div>
      </div>

      <div className="separator" />

      {servicesList.length === 0 && (
        <div className="staff-add-button">
          <button className="add-button" onClick={addService}>+ Add Service</button>
        </div>
      )}

      {servicesList.map((service, index) => (
        <div key={index} className="staff-block">
          <div className="staff-form-grid">
            <div className="dropdown-item3">
              <label>Service Name<span className="required">*</span></label>
              <ServiceSelector
                selected={service.name}
                setSelected={(value) => updateService(index, "name", value)}
                single={true}
              />
            </div>

            <div className="form-field">
              <label>Duration (min)<span className="required">*</span></label>
              <input
                type="number"
                value={service.duration}
                onChange={(e) => updateService(index, "duration", e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Price ($)<span className="required">*</span></label>
              <input
                type="number"
                value={service.price}
                onChange={(e) => updateService(index, "price", e.target.value)}
              />
            </div>

            <div className="dropdown-item3 full-width">
              <label>Machines Used</label>
              <MachineSelector
                selected={service.machinesUsed}
                setSelected={(value) => updateService(index, "machinesUsed", value)}
              />
            </div>

            <div className="dropdown-item3 full-width">
              <label>Products Used</label>
              <ProductSelector
                selected={service.productsUsed || []}
                setSelected={(value) => updateService(index, "productsUsed", value)}
              />
            </div>

            {[0, 1, 2].map((pairIndex) => (
              <div className="form-field" key={pairIndex}>
                <label>Before & After Image {pairIndex + 1}</label>
                <input
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    let currentService = servicesList[index];

                    if (!currentService.id) {
                      const payload = {
                        clinic_id: formData.clinic_id,
                        name: currentService.name,
                        duration: currentService.duration,
                        price: currentService.price,
                        machines_used: currentService.machinesUsed,
                        products_used: currentService.productsUsed,
                        images: currentService.images.filter((img) => img?.file_url),
                      };

                      try {
                        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/services/save-service`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(payload),
                        });

                        const result = await res.json();

                        if (!res.ok) {
                          alert("❌ Failed to save service before upload: " + (result.message || "unknown error"));
                          return;
                        }

                        const updated = [...servicesList];
                        updated[index].id = result.service_id;
                        setServicesList(updated);
                        currentService = updated[index];

                      } catch (err) {
                        alert("❌ Error saving service before upload: " + err.message);
                        return;
                      }
                    }

                    const upload = await handleFileUpload(file, formData.clinic_id, currentService.id, pairIndex);
                    if (upload) {
                      const updated = [...servicesList];
                      updated[index].images[pairIndex] = upload;

                      if (!updated[index].id && currentService.id) {
                        updated[index].id = currentService.id;
                      }
                      setServicesList(updated);
                    }
                  }}
                />
                {service.images[pairIndex]?.file_url && (
                  <div className="small-note">
                    ✅ <a href={service.images[pairIndex].file_url} target="_blank" rel="noopener noreferrer">
                      {service.images[pairIndex].file_name}
                    </a>
                    <button
                    onClick={() => handleDeleteImage(service.id, service.images[pairIndex], index, pairIndex)}
                    style={{
                      color: "red",
                      background: "none",
                      border: "none",
                      fontSize: "16px",
                      cursor: "pointer"
                    }}
                    title="Delete this image"
                  >
                    ✕
                  </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="staff-actions">
            <button className="remove-button" onClick={() => removeService(index)}>- Remove</button>
            {index === servicesList.length - 1 && (
              <button className="add-button" onClick={addService}>+ Add Service</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


